import { useEffect, useState } from "react";
import { BrowserRouter, Link, NavLink, Route, Routes, useLocation } from "react-router-dom";
import { Bot, Menu, X } from "lucide-react";
import { countOnce } from "@/lib/api";
import { HelpersLegal } from "./HelpersLegal";
import DeniedPage from "@/components/world1/DeniedPage";
import { HQLine, useCopy } from "./HelpersUI";
import { JoinModal } from "./JoinModal";
import { HomePage } from "./HomePage";
import { AboutPage, ForeverPage, SolutionsPage, TestimonialsPage } from "./HelpersPages";
import { CareersModal, TrackPage } from "./HelpersExtras";

const NAV = [["/", "Home"], ["/solutions", "Solutions"], ["/forever", "333 Forever"], ["/about", "About"], ["/testimonials", "Testimonials"]];

function Nav({ join }) {
  const { c } = useCopy();
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => { setOpen(false); }, [loc.pathname]);
  const link = ({ isActive }) => `rounded-full px-4 py-2 text-[15px] font-medium transition-colors ${isActive ? "bg-[color:var(--h-mint-soft)] text-[color:var(--h-mint-ink)]" : "text-slate-600 hover:text-[color:var(--h-ink)]"}`;
  return (
    <header className="sticky top-0 z-40 border-b border-[color:var(--h-line)] bg-white/80 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8 lg:h-20">
        <Link to="/" data-testid="helpers-logo" className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[color:var(--h-peach)] text-[color:var(--h-ink)]"><Bot size={20} /></span>
          <span className="h-display text-lg font-bold tracking-tight text-[color:var(--h-ink)]">333 Helpers</span>
        </Link>
        <nav className="hidden items-center gap-1 lg:flex">
          {NAV.map(([to, label]) => <NavLink key={to} to={to} end={to === "/"} className={link} data-testid={`helpers-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}>{label}</NavLink>)}
        </nav>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => join()} data-testid="helpers-nav-join-button"
            className="h-btn hidden rounded-full bg-[color:var(--h-mint-deep)] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[color:var(--h-mint-ink)] sm:inline-flex">{c.join_button}</button>
          <button type="button" onClick={() => setOpen((o) => !o)} data-testid="helpers-menu-toggle" aria-label="Menu" className="rounded-full p-2 text-[color:var(--h-ink)] lg:hidden">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      {open && (
        <nav data-testid="helpers-mobile-menu" className="border-t border-[color:var(--h-line)] bg-white px-5 pb-6 pt-3 lg:hidden">
          {NAV.map(([to, label]) => <NavLink key={to} to={to} end={to === "/"} className={({ isActive }) => `block rounded-2xl px-4 py-3 text-base font-medium ${isActive ? "bg-[color:var(--h-mint-soft)]" : ""}`}>{label}</NavLink>)}
          <button type="button" onClick={() => { setOpen(false); join(); }} className="h-btn mt-3 w-full rounded-full bg-[color:var(--h-mint-deep)] py-3.5 font-semibold text-white">{c.join_button}</button>
        </nav>
      )}
    </header>
  );
}

// "We're here to help. ♡" — the final period is a hidden link to /internal.
function HelpLine({ text }) {
  const i = text.lastIndexOf(".");
  if (i < 0) return <span>{text}</span>;
  return <span>{text.slice(0, i)}<Link to="/internal" data-testid="helpers-internal-link" className="cursor-text text-inherit no-underline" tabIndex={-1} aria-hidden>.</Link>{text.slice(i + 1)}</span>;
}

function Footer() {
  const { c, cfg } = useCopy();
  return (
    <footer data-testid="helpers-footer" className="mt-8 bg-[color:var(--h-mint-soft)]">
      <div className="mx-auto flex max-w-7xl flex-col gap-6 px-5 py-12 sm:px-8 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[color:var(--h-peach)]"><Bot size={18} /></span>
            <span className="h-display font-bold text-[color:var(--h-ink)]">333 Helpers</span>
          </div>
          <p data-testid="helpers-footer-line" className="h-display mt-4 text-xl font-semibold text-[color:var(--h-mint-ink)]"><HelpLine text={c.footer_line || ""} /></p>
        </div>
        <div className="space-y-2 text-sm text-slate-600 md:text-right">
          <div className="flex gap-5 md:justify-end">
            <Link to="/track" data-testid="helpers-track-link" className="hover:text-[color:var(--h-ink)]">Track your Helper</Link>
            <Link to="/privacy" data-testid="helpers-privacy-link" className="hover:text-[color:var(--h-ink)]">Privacy</Link>
            <Link to="/terms" data-testid="helpers-terms-link" className="hover:text-[color:var(--h-ink)]">Terms</Link>
          </div>
          <p data-testid="helpers-billing-contact">{cfg?.billing_contact}</p>
          <HQLine testid="helpers-footer-hq" />
          <p data-testid="helpers-copyright">© 2030 333 Helpers, Inc.</p>
        </div>
      </div>
    </footer>
  );
}

function Internal() {
  return (
    <div data-testid="helpers-internal-page" className="flex h-screen flex-col overflow-hidden bg-[#2a2a2a]">
      <div className="shrink-0 bg-[#d4d4d4] px-5 py-3 font-sans text-[13px] text-[#555]">333 Helpers · Employee portal — authorized personnel only</div>
      <div className="min-h-0 flex-1 overflow-auto"><DeniedPage /></div>
    </div>
  );
}

// Once per visit, at a random moment after 30 s, the page shows its wireframe for two frames.
function useWireframe() {
  useEffect(() => {
    if (sessionStorage.getItem("h_wire") || window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return undefined;
    const id = setTimeout(() => {
      sessionStorage.setItem("h_wire", "1");
      const root = document.documentElement;
      root.classList.add("h-wire");
      requestAnimationFrame(() => requestAnimationFrame(() => requestAnimationFrame(() => root.classList.remove("h-wire"))));
    }, 30000 + Math.random() * 60000);
    return () => clearTimeout(id);
  }, []);
}

function ScrollTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function Site() {
  const [modal, setModal] = useState(null);
  const [careers, setCareers] = useState(false);
  const join = (title) => setModal({ title });
  useWireframe();
  useEffect(() => { countOnce("helpers_visit"); }, []);
  return (
    <div className="h-site min-h-screen overflow-x-clip bg-[color:var(--h-bg)]" data-testid="helpers-site">
      <ScrollTop />
      <Nav join={join} />
      <main>
        <Routes>
          <Route path="/" element={<HomePage join={join} />} />
          <Route path="/solutions" element={<SolutionsPage />} />
          <Route path="/forever" element={<ForeverPage join={join} />} />
          <Route path="/about" element={<AboutPage careers={() => setCareers(true)} />} />
          <Route path="/track" element={<TrackPage />} />
          <Route path="/testimonials" element={<TestimonialsPage />} />
          <Route path="/privacy" element={<HelpersLegal doc="privacy" />} />
          <Route path="/terms" element={<HelpersLegal doc="terms" />} />
          <Route path="*" element={<HomePage join={join} />} />
        </Routes>
      </main>
      <Footer />
      <JoinModal open={!!modal} title={modal?.title} onClose={() => setModal(null)} />
      <CareersModal open={careers} onClose={() => setCareers(false)} />
    </div>
  );
}

export default function HelpersApp() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/internal" element={<Internal />} />
        <Route path="*" element={<Site />} />
      </Routes>
    </BrowserRouter>
  );
}
